import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { sanitizeInput } from "features/Auth/Onboarding/AboutForm/helpers";
import { fetcherAuth } from "utils/api";

import styles from "../styles";
import Collection from "./Collection";
import InstagramEditDialog from "./InstagramEditDialog";
import { InstagramMentionCollection } from "./schema";

interface InstagramConnectedProps {
  closeDialog?: () => void;
}

export default function InstagramConnected({
  closeDialog,
}: InstagramConnectedProps) {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { profile, setProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mentions, setMentions] = useState<InstagramMentionCollection[]>([]);
  const [selectedInstagramMention, setSelectedInstagramMention] =
    useState<InstagramMentionCollection | null>(null);

  const currentUsername = sanitizeInput(profile?.instagramUrl || "");

  const fetchInstagramMentionCollection = async () => {
    if (!currentOrg?.id) return;

    setLoading(true);

    try {
      const { mentions } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/saved-brand-collections/instagram-collection`,
        "GET",
      );
      setMentions(mentions);
    } catch (error) {
      setAlert(error.message || "Failed to fetch instagram mentions", "error");
    } finally {
      setLoading(false);
    }
  };

  const saveInstagramProfile = async (handle: string) => {
    if (!handle || !currentOrg?.id) return;

    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding`,
        "PUT",
        {},
        {
          instagramUrl: `https://instagram.com/${handle}`,
          page: "Instagram Sidebar",
        },
      );

      setProfile((prev) => ({
        ...prev,
        ...organizationProfile,
      }));

      setAlert("Instagram account updated successfully!", "success");
    } catch (error: any) {
      setAlert(error.message || "Failed to update Instagram account", "error");
    }
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  useEffect(() => {
    fetchInstagramMentionCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <>
      <Box>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={styles.header}
        >
          <Grid container item xs={9} alignItems="center">
            <Typography variant="h6" component="div" sx={{ fontSize: 14 }}>
              INSTAGRAM
            </Typography>
          </Grid>

          <Grid container item xs justifyContent="flex-end">
            <Box
              component="button"
              onClick={closeDialog}
              sx={{
                ...styles.close,
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 1,
              }}
            >
              <Box component="i" className="fa-regular fa-xmark" />
            </Box>
          </Grid>
        </Grid>

        <Box sx={styles.content}>
          <Box sx={styles.textSection}>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
              If you tag{" "}
              <Box
                component="a"
                href="https://instagram.com/onbento"
                target="_blank"
                rel="noopener noreferrer"
                sx={styles.link}
              >
                @onbento
              </Box>{" "}
              in a creator's post and we'll add their promoted brands here for
              you to pitch. For Virtual Assistant users, these brands are
              automatically added to next week's upcoming list.{" "}
              <a
                href="https://www.loom.com/share/ec89eca235e34f38b68db2e2de3935b3"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more here
              </a>
              .
            </Typography>

            <Box sx={styles.accountSection}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Connected Instagram Account
              </Typography>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs>
                  <Box sx={styles.usernameDisplay}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      @{currentUsername}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs="auto">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleEditClick}
                    sx={styles.editButton}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-edit"
                      sx={{ mr: 1 }}
                    />
                    Edit
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>

          <Box sx={styles.brandsSection}>
            {loading ? (
              <Box>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Grid key={index} container sx={{ p: 2 }} gap={1}>
                    <Grid item xs={2}>
                      <Skeleton variant="rounded" width={50} height={30} />
                    </Grid>
                    <Grid container item xs gap={1}>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={20}
                        width="100%"
                      />
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={20}
                        width="60%"
                      />
                    </Grid>
                  </Grid>
                ))}
              </Box>
            ) : (
              <>
                {selectedInstagramMention && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedInstagramMention(null);
                      }}
                      color="secondary"
                      sx={{ textTransform: "none", pl: 1 }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-arrow-left"
                        sx={{ mr: 1 }}
                      />
                      Back{" "}
                    </Button>
                    <Collection
                      selectedInstagramMention={selectedInstagramMention}
                    />
                  </>
                )}
                {!selectedInstagramMention &&
                  mentions.map((mention) => (
                    <Grid
                      key={mention.sourceInstagramUsername}
                      container
                      alignItems="center"
                      sx={{
                        cursor: "pointer",
                        py: 2,
                        px: 1,
                        borderBottom: `1px solid ${grey[200]}`,
                      }}
                      onClick={() => {
                        setSelectedInstagramMention(mention);
                      }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography>
                          @{mention.sourceInstagramUsername}
                        </Typography>
                        <Typography variant="body2" sx={{ color: grey[600] }}>
                          {mention.unsentCount === 0 &&
                          mention.sentCount === 0 &&
                          mention.status === "pending"
                            ? "Pending"
                            : `Unsent (${mention.unsentCount}) | Sent (${mention.sentCount})`}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          sx={{
                            fontSize: 12,
                            color: "black",
                            width: 25,
                            height: 25,
                          }}
                        >
                          <Box
                            component="i"
                            className="fa-regular fa-arrow-right"
                          />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                {!selectedInstagramMention &&
                  mentions.length === 0 &&
                  !loading && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic", px: 2 }}
                    >
                      No brands here yet. Tag @onbento on Instagram posts to add
                      brands automatically!
                    </Typography>
                  )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <InstagramEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        currentUsername={currentUsername}
        onSave={saveInstagramProfile}
      />
    </>
  );
}
